import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  api, saveToken, clearToken, getToken, saveCachedUser, getCachedUser,
} from "../api/client";
import { isCapacitorIOS, getCapacitorPlugin } from "../lib/capacitorEnv";

const AuthContext = createContext(undefined);

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

/**
 * Production web callback consumed by the iOS OAuth flow.
 *
 * When the app runs inside Capacitor iOS we hand this URL to Emergent
 * auth as the ``redirect`` parameter. The static page at that URL
 * receives ``#session_id=…`` and immediately jumps to
 * ``myfleet://oauth-callback#session_id=…`` — iOS then hands the URL
 * to our app via the ``appUrlOpen`` event because ``myfleet`` is
 * registered in ``Info.plist / CFBundleURLSchemes``.
 *
 * Using the public HTTPS domain here is required because Google
 * refuses non-https OAuth redirects.
 */
const IOS_OAUTH_BRIDGE_URL = "https://myfleetapp.fr/oauth-callback.html";
const IOS_APP_URL_SCHEME = "myfleet://oauth-callback";

function extractSessionIdFromUrl() {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const match = /session_id=([^&]+)/.exec(hash) || /session_id=([^&]+)/.exec(window.location.search || "");
  return match ? decodeURIComponent(match[1]) : null;
}

function extractSessionIdFromUrlString(urlStr) {
  try {
    const u = new URL(urlStr);
    const hash = u.hash.startsWith("#") ? u.hash.slice(1) : u.hash;
    const params = new URLSearchParams(hash);
    const sid = params.get("session_id");
    if (sid) return sid;
    const q = u.searchParams.get("session_id");
    return q || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Handle Emergent Google auth redirect (#session_id=…)
    if (typeof window !== "undefined") {
      const sid = extractSessionIdFromUrl();
      if (sid) {
        // Account-deletion re-auth interception:
        // If the deletion page armed this flag before signInWithGoogle(),
        // we intercept the fragment WITHOUT calling /auth/google/session —
        // no login, no account linking. The session_id is stashed in
        // sessionStorage (never localStorage, never Mongo) and consumed by
        // the /account/delete page for identity verification only.
        const deleteReauth = window.sessionStorage.getItem("mf_delete_reauth");
        if (deleteReauth === "1") {
          try {
            window.sessionStorage.setItem("mf_delete_google_session_id", sid);
          } catch { /* ignore */ }
          try {
            window.sessionStorage.removeItem("mf_delete_reauth");
          } catch { /* ignore */ }
          try {
            window.history.replaceState(null, "", window.location.pathname);
          } catch { /* ignore */ }
          setLoading(false);
          // Full navigation back to the deletion page — the app is
          // already authenticated (JWT still in localStorage) so the
          // ProtectedRoute lets us through.
          window.location.replace("/account/delete");
          return;
        }
        try {
          const { data } = await api.post("/auth/google/session", { session_id: sid });
          saveToken(data.access_token);
          saveCachedUser(data.user);
          setUser(data.user);
          try {
            window.history.replaceState(null, "", window.location.pathname);
          } catch { /* ignore */ }
          setLoading(false);
          return;
        } catch (e) {
          // fall through
        }
      }
    }
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    const cached = getCachedUser();
    if (cached) setUser(cached);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      saveCachedUser(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        clearToken();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  /**
   * Global listener for iOS deep-links (myfleet://oauth-callback…).
   *
   * Installed once at provider mount. If the Universal Link OR the custom
   * ``myfleet://`` scheme reaches the app while nobody was actively
   * waiting for the OAuth roundtrip, we still exchange the ``session_id``
   * for a JWT. Idempotent — repeated events with the same session_id
   * simply overwrite the JWT.
   *
   * Uses ``window.Capacitor.Plugins.App`` directly because in live-web
   * mode we cannot ``import`` capacitor npm packages — they are not
   * served by ``myfleetapp.fr``. The native iOS shell however injects
   * ``window.Capacitor.Plugins.<Name>`` globally when the plugin was
   * installed via CocoaPods in the iOS project.
   */
  useEffect(() => {
    if (typeof window === "undefined" || !isCapacitorIOS()) return;
    const CapApp = getCapacitorPlugin("App");
    const Browser = getCapacitorPlugin("Browser");
    if (!CapApp || typeof CapApp.addListener !== "function") return;
    let listener = null;
    let cancelled = false;
    (async () => {
      try {
        listener = await CapApp.addListener("appUrlOpen", async (event) => {
          const url = event?.url;
          if (!url) return;
          const sid = extractSessionIdFromUrlString(url);
          if (!sid) return;
          if (Browser && typeof Browser.close === "function") {
            try { await Browser.close(); } catch { /* ignore */ }
          }
          try {
            const { data } = await api.post("/auth/google/session", { session_id: sid });
            saveToken(data.access_token);
            saveCachedUser(data.user);
            setUser(data.user);
          } catch { /* user can retry sign-in */ }
        });
      } catch { /* Capacitor plugin registration failed — nothing to do */ }
    })();
    return () => {
      cancelled = true;
      try { listener?.remove?.(); } catch { /* ignore */ }
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveToken(data.access_token);
    saveCachedUser(data.user);
    setUser(data.user);
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    saveToken(data.access_token);
    saveCachedUser(data.user);
    setUser(data.user);
  }, []);

  const signOut = useCallback(async () => {
    clearToken();
    setUser(null);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    // On the Capacitor iOS shell we cannot navigate the WKWebView to
    // Google's OAuth endpoint — Google refuses ``UIWebView``/``WKWebView``
    // user-agents with ``disallowed_useragent`` and hijacks the flow into
    // the system browser, which never returns to the app. We open an
    // in-app SFSafariViewController via the ``Browser`` plugin (which
    // Google accepts) and route the callback back via the ``myfleet://``
    // URL scheme registered in Info.plist.
    if (isCapacitorIOS()) {
      const Browser = getCapacitorPlugin("Browser");
      const CapApp = getCapacitorPlugin("App");

      // Hard fail-fast guards — surface a clear French error instead of
      // silently swallowing a missing plugin.
      const missing = [];
      if (!Browser || typeof Browser.open !== "function") missing.push("Browser");
      if (!CapApp || typeof CapApp.addListener !== "function") missing.push("App");
      if (missing.length) {
        const details = "Plugins Capacitor manquants: " + missing.join(", ");
        const err = new Error("Google indisponible : " + details + ". Réinstallez l'app depuis TestFlight.");
        err.code = "CAP_PLUGIN_MISSING";
        throw err;
      }

      const redirect = `${IOS_OAUTH_BRIDGE_URL}?to=${encodeURIComponent(IOS_APP_URL_SCHEME)}`;
      const authUrl = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;

      let handled = false;
      let listener = null;
      try {
        listener = await CapApp.addListener("appUrlOpen", async (event) => {
          const url = event?.url;
          if (handled || !url || !url.startsWith("myfleet://")) return;
          const sid = extractSessionIdFromUrlString(url);
          if (!sid) return;
          handled = true;
          try {
            if (typeof Browser.close === "function") await Browser.close();
          } catch { /* ignore */ }
          try {
            const { data } = await api.post("/auth/google/session", { session_id: sid });
            saveToken(data.access_token);
            saveCachedUser(data.user);
            setUser(data.user);
          } catch { /* backend rejection — user can retry */ }
          finally {
            try { await listener?.remove?.(); } catch { /* ignore */ }
          }
        });
      } catch {
        throw new Error("Impossible d'écouter le retour de Google. Réinstallez l'app.");
      }

      try {
        await Browser.open({ url: authUrl, presentationStyle: "popover" });
      } catch (e) {
        try { await listener?.remove?.(); } catch { /* ignore */ }
        throw new Error("Impossible d'ouvrir Google : " + (e?.message || "erreur inconnue"));
      }
      return;
    }
    // Web + Android — classic full-page redirect. Origin is the currently
    // served domain (myfleetapp.fr in production, preview URL in dev),
    // so no hard-coded value ever leaks between environments.
    const origin = window.location.origin + "/";
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(origin)}`;
  }, []);

  /**
   * Sign in with Apple — native iOS flow.
   *
   * Requires the app to run inside the Capacitor iOS shell with the
   * ``@capacitor-community/apple-sign-in`` plugin registered. When the
   * plugin is NOT available (Web, Android, older iOS Safari) this
   * function throws a friendly error so the button can toast the user
   * instead of silently doing nothing.
   *
   * The Apple flow returns an ``identityToken`` (JWT) that we POST to
   * our backend, which verifies it against Apple's JWKS and issues our
   * own JWT — the exact same shape as ``signIn`` / ``signInWithGoogle``.
   */
  const signInWithApple = useCallback(async () => {
    if (!isCapacitorIOS()) {
      const err = new Error("Sign in with Apple n'est disponible que dans l'application iOS.");
      err.code = "APPLE_UNAVAILABLE";
      throw err;
    }
    const SignInWithApple = getCapacitorPlugin("SignInWithApple");
    if (!SignInWithApple || typeof SignInWithApple.authorize !== "function") {
      const err = new Error("Sign in with Apple n'est pas installé dans cette build. Réinstallez l'app depuis TestFlight.");
      err.code = "APPLE_PLUGIN_MISSING";
      throw err;
    }

    // Nonce is generated client-side and never leaves the device except
    // in its SHA-256'd form (Apple hashes it before including it in the
    // identity_token). We pass the raw nonce to the backend so it can
    // compare against ``nonce_supported`` + ``nonce`` claims.
    const rawNonce = (window.crypto?.randomUUID?.() || String(Math.random())).replace(/-/g, "");

    // Native iOS Sign in with Apple options.
    //
    // ``redirectURI`` is intentionally NOT passed: the native flow uses
    // ``ASAuthorizationController`` which returns the credential to the
    // app via a delegate callback — there is no HTTP redirect. Some
    // versions of ``@capacitor-community/apple-sign-in`` reject or
    // silently drop a redirectURI on native platforms.
    const options = {
      clientId: "fr.myfleetapp.app",
      scopes: "email name",
      nonce: rawNonce,
    };

    let result;
    try {
      result = await SignInWithApple.authorize(options);
    } catch (e) {
      const nativeCode = e?.code || e?.errorCode || null;
      const nativeMsgLower = String(e?.message || e?.errorMessage || "").toLowerCase();
      const isCancel =
        nativeCode === "ERR_CANCELED" ||
        nativeCode === "1001" ||
        nativeMsgLower.includes("cancel") ||
        nativeMsgLower.includes("annul");
      if (isCancel) {
        throw new Error("Connexion Apple annulée.");
      }
      const isProfileError =
        nativeCode === "1000" ||
        nativeMsgLower.includes("not handled") ||
        nativeMsgLower.includes("not enabled") ||
        nativeMsgLower.includes("entitlement");
      if (isProfileError) {
        throw new Error("Erreur Apple : la capability ou le provisioning profile est incorrect. Réinstallez l'app depuis TestFlight.");
      }
      throw new Error("Erreur Apple : connexion impossible pour le moment.");
    }

    // Robust extraction — accept every plugin structure variant so that
    // a future plugin upgrade never silently regresses this flow.
    const appleResponse = (result && typeof result === "object" && result.response && typeof result.response === "object")
      ? result.response
      : (result || {});
    const identityToken =
      appleResponse?.identityToken ??
      appleResponse?.identity_token ??
      result?.identityToken ??
      result?.identity_token ??
      null;

    if (!identityToken) {
      const topKeys = result && typeof result === "object" ? Object.keys(result) : null;
      if (!topKeys || topKeys.length === 0) {
        throw new Error("Réponse Apple inattendue (vide). Réessayez.");
      }
      throw new Error("Apple n'a pas retourné d'identifiant valide.");
    }

    const authorizationCode =
      appleResponse?.authorizationCode ??
      appleResponse?.authorization_code ??
      result?.authorizationCode ??
      result?.authorization_code ??
      null;
    const givenName = appleResponse?.givenName ?? appleResponse?.given_name ?? null;
    const familyName = appleResponse?.familyName ?? appleResponse?.family_name ?? null;
    const nameFromApple = [givenName, familyName].filter(Boolean).join(" ").trim() || null;

    try {
      const { data } = await api.post("/auth/apple/session", {
        identity_token: identityToken,
        authorization_code: authorizationCode,
        name: nameFromApple,
        nonce: rawNonce,
      });
      saveToken(data.access_token);
      saveCachedUser(data.user);
      setUser(data.user);
    } catch (e) {
      if (e?.response?.status === 503) {
        throw new Error("Sign in with Apple n'est pas encore activé côté serveur. Contactez le support.");
      }
      if (e?.response?.status === 401) {
        throw new Error("Identifiant Apple refusé par le serveur. Réessayez.");
      }
      throw new Error("Connexion Apple impossible pour le moment.");
    }
  }, []);

  const updateProfile = useCallback(async (name) => {
    const { data } = await api.patch("/auth/me", { name });
    setUser((prev) => {
      const next = prev ? { ...prev, ...data } : data;
      saveCachedUser(next);
      return next;
    });
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, signInWithGoogle, signInWithApple, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
